/**
 * Pagination — cursor-based, because that is what the API does (CONTRACT §7:
 * "All list endpoints paginate with ?cursor=").
 *
 * There are no page numbers to jump to and no total count: an opaque cursor cannot
 * produce either, and inventing one ("1–50 of ~2,000") would be a lie. What the
 * operator gets instead is the current range, Previous, and Next.
 *
 * Pair the component with the hook, which owns the cursor history:
 *
 *   const pager = useCursorPagination();
 *   const [page, setPage] = useState<Page<Run>>({ items: [], cursor: null });
 *
 *   useEffect(() => {
 *     api.runs.list({ cursor: pager.cursor, limit: pager.pageSize }).then(setPage);
 *   }, [pager.cursor, pager.pageSize]);
 *
 *   <Pagination
 *     page={pager.page} pageSize={pager.pageSize} count={page.items.length}
 *     hasNext={Boolean(page.cursor)} hasPrev={pager.hasPrev}
 *     onNext={() => pager.next(page.cursor)} onPrev={pager.prev}
 *     onPageSize={pager.setPageSize} label="runs" />
 *
 * Reset the pager (`pager.reset()`) whenever a filter changes — a cursor from the old
 * filter is meaningless against the new one and the API will reject or mis-page it.
 */

import { useCallback, useMemo, useState } from 'react';

import Button from './Button';

export const PAGE_SIZES = [25, 50, 100, 200] as const;

export interface PaginationProps {
  /** 1-based, from the hook. */
  page: number;
  pageSize: number;
  /** Items actually returned on this page — the last page is usually short. */
  count: number;
  hasNext: boolean;
  hasPrev: boolean;
  onNext: () => void;
  onPrev: () => void;
  /** Omit to hide the page-size control. */
  onPageSize?: (size: number) => void;
  pageSizes?: readonly number[];
  /** Disables both buttons while a fetch is in flight. */
  loading?: boolean;
  /** Plural noun for the range label, e.g. "runs", "grants". */
  label?: string;
}

export function Pagination({
  page,
  pageSize,
  count,
  hasNext,
  hasPrev,
  onNext,
  onPrev,
  onPageSize,
  pageSizes = PAGE_SIZES,
  loading = false,
  label = 'rows',
}: PaginationProps) {
  const range = useMemo(() => {
    // While a fetch is in flight we know nothing about the count, and the skeleton rows
    // above are not an answer. Falling through to the zero branch printed "No schedules"
    // underneath six shimmering rows — two statements on screen at once, one of them
    // false, which reads as a load that failed.
    if (loading) return '—';
    if (count === 0) return `No ${label}`;
    const start = (page - 1) * pageSize + 1;
    const end = start + count - 1;
    return `${start.toLocaleString('en-IN')}–${end.toLocaleString('en-IN')} ${label}`;
  }, [count, label, loading, page, pageSize]);

  return (
    <div className="pagination">
      <div className="pagination-range">
        {range}
        {page > 1 ? <span className="dim"> · page {page}</span> : null}
      </div>

      <div className="pagination-controls">
        {onPageSize ? (
          <label className="pagination-size">
            <span className="dim small">Per page</span>
            <select
              className="select select-sm"
              value={pageSize}
              onChange={(event) => onPageSize(Number(event.target.value))}
              aria-label="Rows per page"
            >
              {pageSizes.map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </label>
        ) : null}

        <div className="btn-group">
          <Button size="sm" onClick={onPrev} disabled={!hasPrev || loading}>
            ← Previous
          </Button>
          <Button size="sm" onClick={onNext} disabled={!hasNext || loading}>
            Next →
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface CursorPager {
  /** Pass to the API call. `undefined` on page 1. */
  cursor: string | undefined;
  /** 1-based page number, derived from how many cursors deep we are. */
  page: number;
  pageSize: number;
  hasPrev: boolean;
  /** Pass the `cursor` from the page you just rendered. A null/empty cursor is a no-op. */
  next: (nextCursor: string | null | undefined) => void;
  prev: () => void;
  setPageSize: (size: number) => void;
  /** Back to page 1. Call this on every filter change. */
  reset: () => void;
}

export function useCursorPagination(initialPageSize = 50): CursorPager {
  // history[i] is the cursor that produced page i+1; page 1 has no cursor.
  const [history, setHistory] = useState<Array<string | undefined>>([undefined]);
  const [pageSize, setSize] = useState(initialPageSize);

  const next = useCallback((nextCursor: string | null | undefined) => {
    if (!nextCursor) return;
    setHistory((current) => [...current, nextCursor]);
  }, []);

  const prev = useCallback(() => {
    setHistory((current) => (current.length > 1 ? current.slice(0, -1) : current));
  }, []);

  const reset = useCallback(() => setHistory([undefined]), []);

  const setPageSize = useCallback((size: number) => {
    setSize(size);
    // A cursor is scoped to the page size that produced it; start over.
    setHistory([undefined]);
  }, []);

  return {
    cursor: history[history.length - 1],
    page: history.length,
    pageSize,
    hasPrev: history.length > 1,
    next,
    prev,
    setPageSize,
    reset,
  };
}

export default Pagination;
